import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// ==================== Initial State ====================
const initialState = {
    safetyPlansUrdu: [],
    currentSafetyPlanUrdu: null,
    isLoading: false,
    error: null,
};

// ==================== Async Thunks ====================

// Fetch all Safety Plan Urdu records
export const fetchSafetyPlansUrdu = createAsyncThunk(
    'safetyPlansUrdu/fetchSafetyPlansUrdu',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get('/safety-plan-urdu');
            return response.data.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.error || 'Failed to fetch Safety Plan Urdu records'
            );
        }
    }
);

// Fetch a single Safety Plan Urdu record
export const fetchSafetyPlanUrdu = createAsyncThunk(
    'safetyPlansUrdu/fetchSafetyPlanUrdu',
    async (id, { rejectWithValue }) => {
        try {
            const response = await api.get(`/safety-plan-urdu/${id}`);
            return response.data.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.error || 'Failed to fetch Safety Plan Urdu record'
            );
        }
    }
);

// Create a new Safety Plan Urdu record
export const createSafetyPlanUrdu = createAsyncThunk(
    'safetyPlansUrdu/createSafetyPlanUrdu',
    async (planData, { rejectWithValue }) => {
        try {
            const response = await api.post('/safety-plan-urdu', planData);
            return response.data.data;
        } catch (error) {
            if (error.response?.status === 422) {
                return rejectWithValue(error.response.data.errors);
            }
            return rejectWithValue(
                error.response?.data?.error || 'Failed to create Safety Plan Urdu record'
            );
        }
    }
);

// Update an existing Safety Plan Urdu record
export const updateSafetyPlanUrdu = createAsyncThunk(
    'safetyPlansUrdu/updateSafetyPlanUrdu',
    async ({ case_id, planData }, { rejectWithValue }) => {
        try {
            const response = await api.put(`/safety-plan-urdu/case/${case_id}`, planData);
            return response.data.data ?? response.data;
        } catch (error) {
            if (error.response?.status === 422) {
                return rejectWithValue(error.response.data.errors);
            }
            return rejectWithValue(
                error.response?.data?.error || 'Failed to update Safety Plan Urdu record'
            );
        }
    }
);

// Delete a Safety Plan Urdu record
export const deleteSafetyPlanUrdu = createAsyncThunk(
    'safetyPlansUrdu/deleteSafetyPlanUrdu',
    async (id, { rejectWithValue }) => {
        try {
            await api.delete(`/safety-plan-urdu/${id}`);
            return id;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.error || 'Failed to delete Safety Plan Urdu record'
            );
        }
    }
);

// ==================== Slice ====================
const safetyPlansUrduSlice = createSlice({
    name: 'safetyPlansUrdu',
    initialState,
    reducers: {
        clearErrorUrdu: (state) => {
            state.error = null;
        },
        setCurrentSafetyPlanUrdu: (state, action) => {
            state.currentSafetyPlanUrdu = action.payload;
        },
        clearCurrentSafetyPlanUrdu: (state) => {
            state.currentSafetyPlanUrdu = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // FETCH ALL
            .addCase(fetchSafetyPlansUrdu.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchSafetyPlansUrdu.fulfilled, (state, action) => {
                state.isLoading = false;
                state.safetyPlansUrdu = action.payload;
            })
            .addCase(fetchSafetyPlansUrdu.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // FETCH SINGLE
            .addCase(fetchSafetyPlanUrdu.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchSafetyPlanUrdu.fulfilled, (state, action) => {
                state.isLoading = false;
                state.currentSafetyPlanUrdu = action.payload;
            })
            .addCase(fetchSafetyPlanUrdu.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // CREATE
            .addCase(createSafetyPlanUrdu.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(createSafetyPlanUrdu.fulfilled, (state, action) => {
                state.isLoading = false;
                state.safetyPlansUrdu.unshift(action.payload);
            })
            .addCase(createSafetyPlanUrdu.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // UPDATE
            .addCase(updateSafetyPlanUrdu.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(updateSafetyPlanUrdu.fulfilled, (state, action) => {
                state.isLoading = false;
                const updated = action.payload.data ?? action.payload;

                const index = state.safetyPlansUrdu.findIndex((d) => d.id === updated.id);
                if (index !== -1) {
                    state.safetyPlansUrdu[index] = updated;
                }

                if (state.currentSafetyPlanUrdu?.id === updated.id) {
                    state.currentSafetyPlanUrdu = updated;
                }
            })
            .addCase(updateSafetyPlanUrdu.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // DELETE
            .addCase(deleteSafetyPlanUrdu.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(deleteSafetyPlanUrdu.fulfilled, (state, action) => {
                state.isLoading = false;
                state.safetyPlansUrdu = state.safetyPlansUrdu.filter(
                    (d) => d.id !== action.payload
                );

                if (state.currentSafetyPlanUrdu?.id === action.payload) {
                    state.currentSafetyPlanUrdu = null;
                }
            })
            .addCase(deleteSafetyPlanUrdu.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            });
    },
});

export const {
    clearErrorUrdu,
    setCurrentSafetyPlanUrdu,
    clearCurrentSafetyPlanUrdu,
} = safetyPlansUrduSlice.actions;

export default safetyPlansUrduSlice.reducer;
